import { useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSkeleton from '../shared/LoadingSkeleton';
import { useNewsletters } from '../../hooks/useApi';
import type { INewsletter } from '../../types';
import { formatDate } from '../../utils/formatDate';

const placeholderSvgLarge = (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
);
const placeholderSvgSmall = (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
);

function InsightImage({ src, alt, large = false }: { src: string; alt: string; large?: boolean }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <div className="insight-placeholder">{large ? placeholderSvgLarge : placeholderSvgSmall}</div>;
  }
  return (
    <div className="insight-image">
      <img src={src} alt={alt} onError={() => setBroken(true)} />
    </div>
  );
}

const typeLabels: Record<string, string> = {
  'defi-update': 'DeFi Update',
  'ai-insights': 'AI Insights',
  'investor-brief': 'Investor Brief',
  'portfolio-spotlight': 'Portfolio Spotlight',
  'rwa-report': 'RWA Report',
  'regulatory-radar': 'Regulatory Radar',
};

function getTypeLabel(newsletter: INewsletter): string {
  if (newsletter.type === 'custom') return newsletter.customType || 'Insight';
  return typeLabels[newsletter.type] || newsletter.type;
}

function getExcerpt(newsletter: INewsletter): string {
  if (newsletter.excerpt) return newsletter.excerpt;
  const firstText = newsletter.sections?.find((s) => s.type === 'text' && s.body);
  if (firstText?.body) {
    const text = firstText.body.replace(/\n/g, ' ').trim();
    return text.length > 160 ? text.slice(0, 160) + '…' : text;
  }
  return '';
}

interface FeaturedCarouselProps {
  newsletters: INewsletter[];
}

function FeaturedCarousel({ newsletters }: FeaturedCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = newsletters.length;
  const current = newsletters[index];

  if (!current) return null;

  return (
    <Link
      to={`/insights/${current.slug}`}
      className="insight-card featured"
    >
      {current.heroImage
        ? <InsightImage src={current.heroImage} alt={current.title} large />
        : <div className="insight-placeholder">{placeholderSvgLarge}</div>
      }

      <div className="insight-tag">🔥 Featured · {getTypeLabel(current)}</div>
      <div className="insight-title">{current.title}</div>

      {getExcerpt(current) && (
        <p className="insight-summary">{getExcerpt(current)}</p>
      )}

      <div className="insight-meta">
        {current.publishedAt && <span>📅 {formatDate(current.publishedAt)}</span>}
        <span>✍️ Apex Research</span>
        <span className="insight-read-more">Read More →</span>
      </div>

      {total > 1 && (
        <div
          className="insight-carousel-controls"
          onClick={(e) => e.preventDefault()}
        >
          <button
            type="button"
            className="insight-carousel-btn"
            onClick={(e) => {
              e.preventDefault();
              setIndex((i) => (i - 1 + total) % total);
            }}
            aria-label="Previous featured article"
          >
            ‹
          </button>
          <span className="insight-carousel-dots">
            {newsletters.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`insight-carousel-dot${i === index ? ' is-active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setIndex(i);
                }}
                aria-label={`Go to article ${i + 1}`}
              />
            ))}
          </span>
          <button
            type="button"
            className="insight-carousel-btn"
            onClick={(e) => {
              e.preventDefault();
              setIndex((i) => (i + 1) % total);
            }}
            aria-label="Next featured article"
          >
            ›
          </button>
        </div>
      )}
    </Link>
  );
}

export default function InsightsSection() {
  const { data: featuredData, isLoading: featuredLoading } = useNewsletters({ featured: true, limit: 5 });
  const { data: regularData, isLoading: regularLoading } = useNewsletters({ limit: 6 });

  const featured = featuredData?.data ?? [];
  const allRecent = regularData?.data ?? [];
  // Regular cards: recent non-featured newsletters (up to 2 for the right column)
  const regular = allRecent.filter((n) => !n.featured).slice(0, 2);

  const isLoading = featuredLoading || regularLoading;

  return (
    <section id="insights" className="insights-section">
      <div style={{ maxWidth: 1200, margin: '0 auto 0' }}>
        <div className="eyebrow-label">Thought Leadership</div>
        <h2>
          Insights &amp; <span>Intelligence</span>
        </h2>
      </div>

      {isLoading ? (
        <div className="insights-grid">
          <LoadingSkeleton className="h-80" />
          <LoadingSkeleton className="h-80" />
          <LoadingSkeleton className="h-80" />
        </div>
      ) : featured.length === 0 && regular.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', marginTop: '3rem', fontSize: '0.9rem' }}>
          No insights published yet. Check back soon.
        </p>
      ) : (
        <div className="insights-grid">
          {featured.length > 0 ? (
            <FeaturedCarousel newsletters={featured} />
          ) : regular.length > 0 ? (
            // If no featured newsletters, promote the first regular one to featured slot
            <Link
              to={`/insights/${regular[0].slug}`}
              className="insight-card featured"
            >
              {regular[0].heroImage
                ? <InsightImage src={regular[0].heroImage} alt={regular[0].title} large />
                : <div className="insight-placeholder">{placeholderSvgLarge}</div>
              }
              <div className="insight-tag">{getTypeLabel(regular[0])}</div>
              <div className="insight-title">{regular[0].title}</div>
              {getExcerpt(regular[0]) && <p className="insight-summary">{getExcerpt(regular[0])}</p>}
              <div className="insight-meta">
                {regular[0].publishedAt && <span>📅 {formatDate(regular[0].publishedAt)}</span>}
                <span className="insight-read-more">Read More →</span>
              </div>
            </Link>
          ) : null}

          {regular.map((newsletter) => (
            <Link
              key={newsletter._id}
              to={`/insights/${newsletter.slug}`}
              className="insight-card"
            >
              {newsletter.heroImage
                ? <InsightImage src={newsletter.heroImage} alt={newsletter.title} />
                : <div className="insight-placeholder">{placeholderSvgSmall}</div>
              }
              <div className="insight-tag">{getTypeLabel(newsletter)}</div>
              <div className="insight-title">{newsletter.title}</div>
              {getExcerpt(newsletter) && (
                <p className="insight-summary">{getExcerpt(newsletter)}</p>
              )}
              <div className="insight-meta">
                {newsletter.publishedAt && <span>📅 {formatDate(newsletter.publishedAt)}</span>}
                <span className="insight-read-more">Read More →</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="insights-footer">
        <Link to="/insights" className="btn-ghost-light">
          Read More Newsletters
        </Link>
      </div>
    </section>
  );
}
